import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Tooltip,
  IconButton,
  Menu,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
  SaveAlt as SaveAltIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useCachedData } from '../hooks/useCachedData';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { api, endpoints } from '../api/config';
import { DIALOG_CLOSE_BUTTON_STYLE, DIALOG_MAX_WIDTH } from '../constants/styles';

const DEFAULT_LOCATIONS = [
  'Flemington L41',
  'L60-Hardeeville_SC',
  'L74-Perris_CA',
];

const DETAILS_COLUMNS = ['item', 'user_name', 'scanned_at', 'tracking_number'];

const ShipConfirmByStatus = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('open');
  const [location, setLocation] = useState('Flemington L41');
  const [locations, setLocations] = useState(DEFAULT_LOCATIONS);
  const { data: locationsData } = useCachedData(endpoints.locations);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({});


  // Dialog states for row click details
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsSearchTerm, setDetailsSearchTerm] = useState('');


  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  // Initialize locations when locationsData is available
  useEffect(() => {
    if (!locationsData) return;

    const list = Array.isArray(locationsData)
      ? locationsData
      : Object.values(locationsData);

    const uniqueLocations = list
      .map((loc) => loc.location || loc.name || loc.id)
      .filter(Boolean);

    if (uniqueLocations.length > 0) {
      const merged = Array.from(new Set([...DEFAULT_LOCATIONS, ...uniqueLocations]));
      setLocations(merged);
    }
  }, [locationsData]);

  // Fetch ship confirmations whenever status or location changes
  useEffect(() => {
    const fetchData = async () => {
      if (!status || !location) return;
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/get-ship-confirm-by-status', {
          params: {
            status,
            location,
          },
        });
        const data =
          response.data?.data ||
          response.data?.items ||
          response.data ||
          [];

        const arrayData = Array.isArray(data) ? data : Object.values(data || {});
        const withIds = arrayData.map((r, idx) => ({
          id: r.id ?? r._id ?? idx,
          ...r,
        }));
        setRecords(withIds);
      } catch (err) {
        console.error('Error fetching ship confirmations by status:', err);
        setRecords([]);
        setError(
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'Failed to load ship confirmations'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [status, location]);


  // Initialize/update visible columns based on data keys, excluding id and shipment_confirm_id
  useEffect(() => {
    if (Array.isArray(records) && records.length > 0) {
      const keySet = new Set();
      records.forEach((r) =>
        Object.keys(r || {}).forEach((k) => {
          // Exclude id and shipment_confirm_id columns
          if (k !== 'id' && k !== 'shipment_confirm_id' && k !== '_id') {
            keySet.add(k);
          }
        })
      );
      const keys = Array.from(keySet);
      setVisibleColumns((prev) => {
        const next = { ...prev };
        keys.forEach((k) => {
          if (typeof next[k] === 'undefined') next[k] = true;
        });
        return next;
      });
    }
  }, [records]);

  // Derive columns from response keys, excluding id and shipment_confirm_id
  const allKeys = useMemo(() => {
    if (!Array.isArray(records) || records.length === 0) return [];
    const keySet = new Set();
    records.forEach((r) =>
      Object.keys(r || {}).forEach((k) => {
        // Exclude id and shipment_confirm_id columns
        if (k !== 'id' && k !== 'shipment_confirm_id' && k !== '_id') {
          keySet.add(k);
        }
      })
    );
    return Array.from(keySet);
  }, [records]);

  const swapKeys = (keys, keyA, keyB) => {
    const idxA = keys.indexOf(keyA);
    const idxB = keys.indexOf(keyB);
    if (idxA === -1 || idxB === -1) return keys;
    const next = [...keys];
    next[idxA] = keyB;
    next[idxB] = keyA;
    return next;
  };

  const visibleKeys = useMemo(() => {
    const keys = allKeys.filter((k) => visibleColumns[k] !== false);
    return swapKeys(keys, 'pickup_date', 'arrival_time');
  }, [allKeys, visibleColumns]);

  const labelForColumn = (key) => {
    if (!key || typeof key !== 'string') return 'Unknown';
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Filter data based on search term across all fields
  const filteredData = useMemo(() => {
    let data = records;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = records.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(term)
      );
    }
    return data;
  }, [searchTerm, records]);



  const mainGridColumns = useMemo(
    () =>
      visibleKeys.map((key) => ({
        accessorKey: key,
        header: labelForColumn(key),
        cell: (info) => {
          const value = info.getValue();
          if (value === null || value === undefined || value === 'null' || value === '') return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        },
      })),
    [visibleKeys]
  );

  const labelForDetailsColumn = (key) => {
    const labels = {
      'item': 'Item',
      'user_name': 'User Name',
      'scanned_at': 'Scanned At',
      'tracking_number': 'Tracking Number',
    };
    return labels[key] || key;
  };

  const detailsGridColumns = useMemo(
    () =>
      DETAILS_COLUMNS.map((key) => ({
        accessorKey: key,
        header: labelForDetailsColumn(key),
        cell: (info) => {
          const value = info.getValue();
          if (value === null || value === undefined || value === 'null' || value === '') return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        },
      })),
    []
  );

  // Filter details data based on search term
  const filteredDetailsData = useMemo(() => {
    if (!detailsData || detailsData.length === 0) return [];
    let data = detailsData;
    if (detailsSearchTerm) {
      const term = detailsSearchTerm.toLowerCase();
      data = detailsData.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(term)
      );
    }
    return data;
  }, [detailsData, detailsSearchTerm]);

  const shipConfirmMainGrid = useDataGridProTable({
    data: filteredData,
    loading,
    error: error || null,
    title: 'Ship Confirm Records',
    columns: mainGridColumns,
    onRowClick: handleRowClick,
    getRowId: (row) => row?.id ?? row?._id ?? row?.shipment_confirm_id ?? JSON.stringify(row),
    initialColumnVisibility: visibleColumns,
    onColumnVisibilityChange: setVisibleColumns,
    hideQuickFilter: true,
    searchOnEnter: false,
    disablePaper: true,
  });

  const shipConfirmDetailsGrid = useDataGridProTable({
    data: filteredDetailsData,
    loading: detailsLoading,
    error: detailsError || null,
    title: 'Shipment Confirm Items',
    columns: detailsGridColumns,
    getRowId: (row, index) =>
      `${row?.item || ''}|${row?.user_name || ''}|${row?.tracking_number || ''}|${index}`,
    hideQuickFilter: true,
    searchOnEnter: false,
    disablePaper: true,
  });

  const handleColumnMenuOpen = (e) => setColumnMenuAnchorEl(e.currentTarget);
  const handleColumnMenuClose = () => setColumnMenuAnchorEl(null);
  const toggleColumn = (key) =>
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));

  // Extract shipment_confirm_id from row (checking various field name variations)
  function getShipmentConfirmId(row) {
    if (!row) return null;

    // Check various possible field names
    const possibleKeys = [
      'shipment_confirm_id',
      'Shipment Confirm Id',
      'shipmentConfirmId',
      'id',
      'shipment_id',
      'Shipment Id',
      'shipmentId',
    ];

    for (const key of possibleKeys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
        return String(row[key]);
      }
    }

    return null;
  }

  // Handle row click to fetch and display details
  async function handleRowClick(row) {
    if (!row) return;

    const shipmentConfirmId = getShipmentConfirmId(row);
    if (!shipmentConfirmId) {
      console.warn('No shipment confirm ID found in row:', row);
      return;
    }

    setDetailsDialogOpen(true);
    setSelectedRow(row);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsData([]);
    setDetailsSearchTerm('');

    try {
      const response = await api.get('/get-shipment-confirm-submit-by-id', {
        params: {
          shipment_confirm_id: shipmentConfirmId,
        },
      });

      console.log('API Response:', response);
      console.log('Response Data:', response.data);
      console.log('Response Data Type:', typeof response.data);
      console.log('Is Array:', Array.isArray(response.data));
      console.log('Response.data.data:', response.data?.data);
      console.log('Response.data.data Type:', typeof response.data?.data);
      console.log('Response.data.data Is Array:', Array.isArray(response.data?.data));

      // Helper function to recursively find arrays in an object
      const findArrayInObject = (obj, depth = 0, maxDepth = 3) => {
        if (depth > maxDepth) return null;
        if (Array.isArray(obj)) return obj;
        if (typeof obj !== 'object' || obj === null) return null;

        for (const value of Object.values(obj)) {
          if (Array.isArray(value)) {
            return value;
          }
          if (typeof value === 'object' && value !== null) {
            const found = findArrayInObject(value, depth + 1, maxDepth);
            if (found) return found;
          }
        }
        return null;
      };

      // Handle various response formats
      let data = [];

      // If response.data is an array directly
      if (Array.isArray(response.data)) {
        data = response.data;
      }
      // If response.data.data is an array (most common case based on logs)
      else if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (typeof response.data.data === 'object') {
          // Recursively search for arrays in the object
          const foundArray = findArrayInObject(response.data.data);
          if (foundArray) {
            data = foundArray;
          } else {
            // If it's a single object, wrap it in array
            data = [response.data.data];
          }
        }
      }
      // If response.data.items is an array
      else if (response.data?.items && Array.isArray(response.data.items)) {
        data = response.data.items;
      }
      // If response.data is an object, try to extract array from it
      else if (response.data && typeof response.data === 'object') {
        // Recursively search for arrays
        const foundArray = findArrayInObject(response.data);
        if (foundArray) {
          data = foundArray;
        } else {
          // Check if any property is an array
          const arrayValue = Object.values(response.data).find(val => Array.isArray(val));
          if (arrayValue) {
            data = arrayValue;
          } else {
            // If it's a single object, wrap it in array
            data = [response.data];
          }
        }
      }

      console.log('Extracted Data:', data);
      console.log('Data Length:', data.length);
      console.log('First Item:', data[0]);
      console.log('All Data Keys:', data.length > 0 ? Object.keys(data[0] || {}) : []);
      console.log('First Item:', data[0]);

      setDetailsData(data);
    } catch (err) {
      console.error('Error fetching shipment confirm details:', err);
      console.error('Error Response:', err.response);
      setDetailsError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load shipment confirm details'
      );
    } finally {
      setDetailsLoading(false);
    }
  }



  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Header */}
            <Typography
              variant="h5"
              component="h1"
              sx={{
                mb: 1.5,
                fontWeight: 'bold',
                color: 'primary.main',
                fontSize: '1.25rem',
              }}
            >
              Ship Confirm
            </Typography>

            {/* Filters row - search, status, location */}
            <Box
              sx={{
                mb: 1.5,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <TextField
                  fullWidth
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '0.875rem' },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: { xs: 140, sm: 160 } }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                  }}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      height: '36px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <MenuItem value="open" sx={{ fontSize: '0.875rem' }}>
                    Open
                  </MenuItem>
                  <MenuItem value="closed" sx={{ fontSize: '0.875rem' }}>
                    Closed
                  </MenuItem>
                </TextField>
              </Box>
              <Box sx={{ minWidth: { xs: 180, sm: 220 } }}>
                <TextField
                  select
                  fullWidth
                  label="Location"
                  value={location}
                  onChange={(e) => {
                    setLocation(e.target.value);
                  }}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      height: '36px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  {locations.map((loc) => (
                    <MenuItem key={loc} value={loc} sx={{ fontSize: '0.875rem' }}>
                      {loc}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {/* Data Table */}
            <Paper elevation={2} sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  p: 0.5,
                  px: 1,
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ fontSize: '1.125rem' }}
                >
                  Ship Confirm Records
                </Typography>
                <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, gap: 0.5, flexWrap: 'wrap' }}>

                  <GlobalDenseModeToolbar
                    denseMode={denseMode}
                    denseModeTogglePending={denseModeTogglePending}
                    persistDenseMode={persistDenseMode}
                  />
                  <Tooltip title="Show/Hide Columns">
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      aria-label="Show/Hide Columns"
                      sx={{ padding: '6px' }}
                    >
                      <ViewColumnIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={columnMenuAnchorEl}
                    open={Boolean(columnMenuAnchorEl)}
                    onClose={handleColumnMenuClose}
                    PaperProps={{
                      sx: { maxHeight: '70vh', minWidth: 220, p: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1,
                        pb: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}
                      >
                        Show/Hide Columns
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleColumnMenuClose}
                        sx={{ p: 0.5 }}
                        aria-label="Close column visibility menu"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {allKeys.map((k) => (
                      <MenuItem
                        key={k}
                        onClick={() => toggleColumn(k)}
                        sx={{
                          fontSize: '0.875rem',
                          minHeight: 'auto',
                          py: 0.75,
                          px: 1,
                        }}
                      >
                        <Checkbox
                          checked={visibleColumns[k] !== false}
                          size="small"
                          sx={{ p: 0.5, mr: 1 }}
                        />
                        <ListItemText
                          primary={labelForColumn(k)}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { lineHeight: 1.2 },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                  <Tooltip title="Download CSV">
                    <IconButton
                      onClick={() => {
                        if (!filteredData.length || visibleKeys.length === 0) return;
                        const header = visibleKeys
                          .map((k) => labelForColumn(k))
                          .join(',');
                        const process = (val) => {
                          if (val === null || val === undefined || val === 'null' || val === '') return '';
                          if (typeof val === 'object')
                            return JSON.stringify(val).replace(/"/g, '""');
                          return String(val).replace(/"/g, '""');
                        };
                        const rows = filteredData.map((row) =>
                          visibleKeys
                            .map((k) => `"${process(row[k])}"`)
                            .join(',')
                        );
                        const csv = [header, ...rows].join('\n');
                        const blob = new Blob([csv], {
                          type: 'text/csv;charset=utf-8;',
                        });
                        const link = document.createElement('a');
                        const url = URL.createObjectURL(blob);
                        link.setAttribute('href', url);
                        link.setAttribute('download', 'ship_confirm_records.csv');
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      size="small"
                      aria-label="Download CSV"
                      sx={{ padding: '6px' }}
                    >
                      <SaveAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ px: 1, pb: 1, minHeight: 0, flex: 1 }}>
                <MuiXDataGridSurface hook={shipConfirmMainGrid} DataGridPro={DataGridPro} />
              </Box>
            </Paper>
          </Box>
      </Box>

      {/* Details Dialog - shows items for a clicked row */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth={DIALOG_MAX_WIDTH}
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography variant="h6" component="div">
            Shipment Confirm Details {selectedRow && getShipmentConfirmId(selectedRow) && `- ${getShipmentConfirmId(selectedRow)}`}
          </Typography>
          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            size="small"
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'hidden', p: 0, display: 'flex', flexDirection: 'column' }}>
          {detailsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : detailsError ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: 400,
              }}
            >
              <Typography color="error">{detailsError}</Typography>
            </Box>
          ) : detailsData && Array.isArray(detailsData) && detailsData.length > 0 ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.5 }}>
              {/* Search Bar */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder="Search"
                  value={detailsSearchTerm}
                  onChange={(e) => {
                    setDetailsSearchTerm(e.target.value);
                  }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '0.875rem' },
                  }}
                />
              </Box>

              {/* Data Table */}
              <Paper elevation={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 1 }}>
                <MuiXDataGridSurface hook={shipConfirmDetailsGrid} DataGridPro={DataGridPro} />
              </Paper>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: 400,
                p: 2,
              }}
            >
              <Typography>No details available</Typography>
              {detailsData && (
                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                  Data received: {Array.isArray(detailsData) ? `${detailsData.length} items` : typeof detailsData}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default ShipConfirmByStatus;

